import {createSelector} from 'reselect';
import {LAUNCHER} from '../../constants/Catalog';
import {selectCatalogItems} from './catalog.selectors';
import {isDLCCatalogItem} from '../../utils/CatalogUtils';

export const selectProductInstallStates = state => state.getIn(['product_v2', 'installStates']);
export const selectProductUserBuildEntitlements = state =>
    state.getIn(['product_v2', 'userBuildEntitlements']);
export const selectProductButtonDisabledSettings = state =>
    state.getIn(['product_v2', 'buttonDisabledSettings']);
export const selectProductSettings = state => state.getIn(['product_v2', 'productSettings']);
export const selectProductGlobalAutoUpdateSetting = state =>
    state.getIn(['product_v2', 'globalAutoUpdateSetting']);
export const selectProductNamespaceMapping = state =>
    state.getIn(['product_v2', 'namespaceMapping']);
export const selectUserOfferEntitlements = state =>
    state.getIn(['product_v2', 'userOfferEntitlements']);
export const selectProductDLCConfigs = state => state.getIn(['product_v2', 'productDLCConfigs']);
export const selectProductBuildPrerequisites = state =>
    state.getIn(['product_v2', 'buildPrerequisites']);
export const selectProductPages = state => state.getIn(['product', 'productPages']);

export const selectProductNonDLCInstallStates = createSelector(
    selectProductInstallStates,
    selectCatalogItems,
    (installStates, catalogItems) =>
        installStates.filter(x => !isDLCCatalogItem(catalogItems.get(x.catalogid)))
);

export const selectProductNameByNamespace = createSelector(
    (_, props) => props && props.namespace,
    selectProductNamespaceMapping,
    (namespace, mapping) => namespace && mapping && mapping.get(namespace)
);

export const selectNamespaceByProductName = createSelector(
    (_, props) => props && props.productName,
    selectProductNamespaceMapping,
    (productName, mapping) => productName && mapping && mapping.flip().get(productName)
);

export const selectProductInstallStateById = createSelector(
    (_, props) => props.catalogId,
    selectProductInstallStates,
    (catalogId, productInstallStates) => productInstallStates && productInstallStates.get(catalogId)
);

export const selectProductSettingsById = createSelector(
    (_, props) => props && props.catalogId,
    selectProductSettings,
    (catalogId, settings) => catalogId && settings && settings.get(catalogId)
);

export const selectIsUserEntitledToBuild = createSelector(
    (_, props) => props.catalogId,
    selectProductUserBuildEntitlements,
    (catalogId, buildEntitlements) => {
        if (!catalogId) return false;
        if (catalogId === LAUNCHER) return true; // always entitled to launcher
        return buildEntitlements && buildEntitlements.get(catalogId, false);
    }
);

export const selectIsProductButtonDisabled = createSelector(
    (_, props) => props.catalogId,
    selectProductButtonDisabledSettings,
    (catalogId, buttonDisabledSettings) =>
        catalogId && buttonDisabledSettings && buttonDisabledSettings.get(catalogId, false)
);

export const selectCanProductAutoUpdate = createSelector(
    (_, props) => props.catalogId,
    selectProductSettings,
    (catalogId, productSettings) => {
        const settings = productSettings && productSettings.get(catalogId);
        return (settings && settings.autoUpdate) || false;
    }
);

export const selectProductHasPrerequisites = createSelector(
    (_, props) => props.offerId,
    selectProductBuildPrerequisites,
    (offerId, prerequisites) =>
        // this should return true | false | undefined
        offerId && prerequisites && prerequisites.get(offerId)
);

export const selectProductMetaByProductNameAndSlug = createSelector(
    (_, props) => props && props.productName,
    (_, props) => props && props.productSlug,
    selectProductPages,
    (productName, productSlug, productPages) => {
        const productPage =
            productPages.getIn([productName, 'pages', productSlug]) ||
            productPages.getIn([productName, 'pages', 'home']);
        if (!productPage) return;
        const metaImage = productPage.getIn(['data', 'meta', 'image']);
        return {
            metaProductImage: metaImage && metaImage.toJS(),
            metaProductName: productPage.get('productName')
        };
    }
);
